/**
 * Instructions Hooks
 * Jotai atoms and hooks for instruction management
 */

import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { instructionService } from '@/services';
import { invalidateQueries } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import type { CreateInstructionDto, UpdateInstructionDto } from '@/types';

// Query atoms
const instructionsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['instructions', buScopeKey(get)],
  queryFn: () => instructionService.getInstructions(buScopeParams(get))
}));

// Mutation atoms
const createInstructionMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['instructions', 'create'],
  mutationFn: (data: CreateInstructionDto) => instructionService.createInstruction(data, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['instructions']);
  }
}));

const updateInstructionMutationAtom = atomWithMutation(() => ({
  mutationKey: ['instructions', 'update'],
  mutationFn: ({ id, data }: { id: string; data: UpdateInstructionDto }) => instructionService.updateInstruction(id, data),
  onSuccess: (_data, variables) => {
    invalidateQueries(['instructions']);
    invalidateQueries(['instructions', variables.id]);
  }
}));

const toggleInstructionStatusMutationAtom = atomWithMutation(() => ({
  mutationKey: ['instructions', 'toggle-status'],
  mutationFn: ({ id, status }: { id: string; status: boolean }) => instructionService.toggleStatus(id, status),
  onSuccess: (_data, variables) => {
    invalidateQueries(['instructions']);
    invalidateQueries(['instructions', variables.id]);
  }
}));

const deleteInstructionMutationAtom = atomWithMutation(() => ({
  mutationKey: ['instructions', 'delete'],
  mutationFn: (id: string) => instructionService.deleteInstruction(id),
  onSuccess: () => {
    invalidateQueries(['instructions']);
  }
}));

// Hooks
export function useInstructions() {
  const [query] = useAtom(instructionsQueryAtom);
  return query;
}

export function useCreateInstruction() {
  const [mutation] = useAtom(createInstructionMutationAtom);
  return mutation;
}

export function useUpdateInstruction() {
  const [mutation] = useAtom(updateInstructionMutationAtom);
  return mutation;
}

export function useToggleInstructionStatus() {
  const [mutation] = useAtom(toggleInstructionStatusMutationAtom);
  return mutation;
}

export function useDeleteInstruction() {
  const [mutation] = useAtom(deleteInstructionMutationAtom);
  return mutation;
}
